import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SEO from "../../components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../../components/Breadcrumbs";
import ChartContainer from "../../components/humanoid-infographics/ChartContainer";
import ChartDataTable from "../../components/humanoid-infographics/ChartDataTable";
import ChartDownload from "../../components/humanoid-infographics/ChartDownload";
import ChartFilters from "../../components/humanoid-infographics/ChartFilters";
import ChartMethodology from "../../components/humanoid-infographics/ChartMethodology";
import ChartSourceList from "../../components/humanoid-infographics/ChartSourceList";
import HumanoidInfographicChart from "../../components/humanoid-infographics/HumanoidInfographicChart";
import InfographicHeader from "../../components/humanoid-infographics/InfographicHeader";
import RelatedInfographics from "../../components/humanoid-infographics/RelatedInfographics";
import { humanoidInfographicBySlug, humanoidInfographicIndex } from "../../data/humanoid-infographics";

export default function HumanoidInfographicPage({ slug: explicitSlug }) {
  const { infographicSlug } = useParams();
  const resolvedSlug = explicitSlug || infographicSlug;
  const dataset = humanoidInfographicBySlug[resolvedSlug];
  const [filterState, setFilterState] = useState({ slug: resolvedSlug, values: {} });
  const filters = useMemo(() => filterState.slug === resolvedSlug ? filterState.values : {}, [filterState, resolvedSlug]);

  const filteredEntries = useMemo(() => {
    if (!dataset) return [];
    return dataset.entries.filter((entry) => Object.entries(filters).every(([key, value]) => !value || String(entry[key]) === value));
  }, [dataset, filters]);

  if (!dataset) return null;

  const breadcrumbs = [{ label: "Home", to: "/" }, { label: "Robotics", to: "/robotics" }, { label: "Humanoid robot data", to: "/robotics/humanoid-robot-data" }, { label: dataset.title, to: dataset.path }];
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: dataset.title,
    description: dataset.seoDescription,
    datePublished: dataset.publishedDate,
    dateModified: dataset.lastUpdated,
    mainEntityOfPage: `https://www.techniahqservice.com${dataset.path}`,
    author: { "@type": "Organization", name: "TechniaHQRobot", url: "https://www.techniahqservice.com/about-techniahq" },
    publisher: { "@type": "Organization", name: "TechniaHQRobot", url: "https://www.techniahqservice.com/" },
    keywords: dataset.primaryKeyword,
  };
  const faqSchema = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: dataset.faq.map((item) => ({ "@type": "Question", name: item.question, acceptedAnswer: { "@type": "Answer", text: item.answer } })) };
  const filterFields = dataset.filters.map((key) => dataset.fields.find((field) => field.key === key) || { key, label: key.replace(/([A-Z])/g, " $1").replace(/^./, (letter) => letter.toUpperCase()) });
  const related = [...humanoidInfographicIndex].sort((a, b) => Number(b.category === dataset.category) - Number(a.category === dataset.category));

  return (
    <>
      <SEO title={dataset.seoTitle} description={dataset.seoDescription} path={dataset.path} type="article" noindex={!dataset.indexable} publishedTime={dataset.publishedDate} modifiedTime={dataset.lastUpdated} author="TechniaHQRobot" section="Humanoid robot data" tags={[dataset.primaryKeyword, dataset.category]} schema={[articleSchema, buildBreadcrumbSchema(breadcrumbs), faqSchema]} />
      <Breadcrumbs items={breadcrumbs} />
      <div>
        <InfographicHeader dataset={dataset} />
        <div className="site-shell grid gap-8 py-10 sm:py-14">
          <section className="soft-card p-6 sm:p-8">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Data summary</p>
            <h2 className="mt-3 text-2xl font-black text-neutral-950">What is included</h2>
            <p className="mt-4 text-base leading-8 text-neutral-700">{dataset.summary}</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl bg-neutral-50 p-4"><p className="text-xs font-black uppercase tracking-[0.14em] text-neutral-500">Visible rows</p><p className="mt-2 text-3xl font-black text-neutral-950">{filteredEntries.length}</p></div>
              <div className="rounded-2xl bg-neutral-50 p-4"><p className="text-xs font-black uppercase tracking-[0.14em] text-neutral-500">Dataset scope</p><p className="mt-2 font-black text-neutral-950">{dataset.datasetLabel}</p></div>
            </div>
          </section>

          <ChartContainer title={dataset.title} description={dataset.chartDescription} actions={<ChartDownload slug={dataset.slug} fields={dataset.fields} entries={filteredEntries} />}>
            <ChartFilters fields={filterFields} entries={dataset.entries} filters={filters} onChange={(key, value) => setFilterState({ slug: resolvedSlug, values: { ...filters, [key]: value } })} />
            {Object.values(filters).some(Boolean) && <button type="button" className="mt-4 font-bold text-blue-700 hover:underline" onClick={() => setFilterState({ slug: resolvedSlug, values: {} })}>Clear filters</button>}
            <div className="mt-6 rounded-3xl border border-neutral-200 bg-white p-4 sm:p-6">{dataset.entries.length > 0 && filteredEntries.length === 0 ? <p className="py-8 text-center text-neutral-600">No records match these filters. Clear filters to restore the dataset.</p> : <HumanoidInfographicChart dataset={dataset} entries={filteredEntries} />}</div>
          </ChartContainer>

          <section className="soft-card p-6 sm:p-8">
            <h2 className="text-2xl font-black text-neutral-950">Accessible data table</h2>
            <p className="mt-3 leading-7 text-neutral-600">The table contains the same source-linked records used by the chart. It is the reference when labels overlap or a visual scale compresses values.</p>
            <div className="mt-6"><ChartDataTable fields={dataset.fields} entries={filteredEntries} /></div>
          </section>

          <ChartMethodology methodology={dataset.methodology} />

          <article className="soft-card p-6 sm:p-10">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-700">Editorial analysis</p>
            {dataset.articleSections.map((section) => (
              <section key={section.heading} className="mt-9 first:mt-4">
                <h2 className="text-2xl font-black tracking-tight text-neutral-950">{section.heading}</h2>
                {section.paragraphs.map((paragraph, index) => <p key={index} className="mt-4 text-base leading-8 text-neutral-700">{paragraph}</p>)}
              </section>
            ))}
          </article>

          <section className="soft-card p-6 sm:p-8">
            <h2 className="text-2xl font-black text-neutral-950">Frequently asked questions</h2>
            <div className="mt-5 divide-y divide-neutral-200">
              {dataset.faq.map((item) => <details key={item.question} className="py-4"><summary className="cursor-pointer font-black text-neutral-950">{item.question}</summary><p className="mt-3 max-w-4xl leading-7 text-neutral-700">{item.answer}</p></details>)}
            </div>
          </section>

          <ChartSourceList sources={dataset.sources} />
          <RelatedInfographics items={related} currentSlug={dataset.slug} />
          <section className="rounded-3xl bg-neutral-950 p-7 text-white sm:p-10">
            <h2 className="text-2xl font-black">Continue with the source-linked humanoid catalog</h2>
            <p className="mt-3 max-w-3xl leading-7 text-neutral-300">Open individual robot records for manufacturer, origin, dimensions, availability, public price status and official product links.</p>
            <div className="mt-6 flex flex-wrap gap-3"><Link to="/humanoid-robots" className="btn-primary">Browse humanoid robots</Link><Link to="/robot-types/humanoid-robots" className="rounded-full border border-white/25 px-5 py-3 text-sm font-black hover:border-white">Read the main guide</Link></div>
          </section>
        </div>
      </div>
    </>
  );
}
